import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionBoqDetail } from './hooks/useConstructionBoqDetail';
import { useConstructionBoqItems } from './hooks/useConstructionBoqItems';
import { Button, Badge, Card, FeaturePlaceholder, KPICard } from '@/components/ui';
import { BoqStatusBadge } from './components/BoqStatusBadge';
import { BoqKPISection } from './components/BoqKPISection';
import { BoqCostSummary } from './components/BoqCostSummary';
import { BoqTreeTable } from './components/BoqTreeTable';
import { BoqActualProgressTable } from './components/BoqActualProgressTable';
import { BoqVariationPanel } from './components/BoqVariationPanel';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  reviewing: 'Đang soát xét',
  approved: 'Đã duyệt',
  locked: 'Đã khóa',
  revised: 'Đã điều chỉnh',
  cancelled: 'Đã hủy',
};

export default function BoqDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { boq: item, isLoading: isBoqLoading, updateBoq } = useConstructionBoqDetail(id);
  const { items, stats, isLoading: isItemsLoading, updateItem } = useConstructionBoqItems(id);
  const isLoading = isBoqLoading || isItemsLoading;

  const [activeTab, setActiveTab] = useState('items');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết dự toán BOQ...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin BOQ</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateBoq({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái BOQ sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Hạng mục' },
    { value: 'by_zone', label: 'Theo khu vực' },
    { value: 'by_phase', label: 'Theo giai đoạn' },
    { value: 'actuals', label: 'Khối lượng thực tế' },
    { value: 'variations', label: 'Phát sinh' },
    { value: 'versions', label: 'Phiên bản' },
    { value: 'documents', label: 'Tài liệu' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.boq_name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <BoqStatusBadge status={item.status_code} />
          <span className="text-sm text-slate-400 font-mono">#{item.boq_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Dự toán BOQ', path: '/construction/boq' },
        { label: item.boq_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'reviewing' && (
            <AuthButton module="boq" action="approve" variant="primary" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
              Phê duyệt BOQ
            </AuthButton>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="boq" action="update" variant="primary" icon="ri-lock-line" onClick={() => handleStatusChange('locked')}>
              Khóa BOQ
            </AuthButton>
          )}
          <AuthButton module="boq" action="export" variant="outline" icon="ri-file-pdf-line">Xuất PDF</AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <BoqKPISection 
                stats={{
                  totalValue: item.total_amount,
                  totalCount: items.length,
                  materialShare: stats?.materialShare || 0,
                  laborShare: stats?.laborShare || 0,
                  equipmentShare: stats?.equipmentShare || 0
                }}
                variant="detail"
              />

              <BoqCostSummary stats={{
                materialShare: stats?.materialShare || 0,
                laborShare: stats?.laborShare || 0,
                equipmentShare: stats?.equipmentShare || 0,
                subcontractorShare: 0
              }} />

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-slate-400" />
                  Thông tin chung
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Mã hợp đồng liên kết</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.contract?.contract_code || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người lập dự toán</span>
                    <span className="text-sm font-bold text-slate-700">Senior QS</span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white rounded-[32px]">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-history-line" />
                  Nhật ký Phiên bản
                </h4>
                <div className="space-y-4">
                  <div className="relative pl-6 border-l border-slate-700 pb-4">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <p className="text-[10px] text-slate-400 font-bold ">v1.0 - Approved</p>
                    <p className="text-xs text-slate-300">Duyệt bởi PM ngày 15/04/2026</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-6 text-os-primary text-[10px] font-bold tracking-widest"
                  onClick={() => navigate(`/construction/boq/versions/${id}`)}
                >
                  Xem toàn bộ lịch sử
                </Button>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <Card className="overflow-hidden border-none shadow-sm rounded-[32px]">
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Hạng mục BOQ chi tiết ({items.length})</h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" icon="ri-file-excel-line">Xuất Excel</Button>
                <AuthButton module="boq" action="update" variant="outline" size="sm" icon="ri-add-line">Thêm hạng mục</AuthButton>
              </div>
            </div>
            <BoqTreeTable 
              items={items} 
              onUpdateItem={async (itemId, updates) => {
                await updateItem({ id: itemId, updates });
                toast.success('Đã cập nhật khối lượng/đơn giá thành công');
              }}
            />
          </Card>
        )}

        {activeTab === 'actuals' && (
          <Card className="overflow-hidden border-none shadow-sm rounded-[32px]">
             <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-sm font-bold text-slate-700">Tiến độ thực tế</h3>
             </div>
             <BoqActualProgressTable items={items} onRecordProgress={(item) => toast.info(`Ghi nhận cho ${item.item_name}`)} />
          </Card>
        )}

        {activeTab === 'variations' && (
           <div className="max-w-3xl">
              <BoqVariationPanel variations={[]} onManage={() => navigate(`/construction/boq/variations/${id}`)} />
           </div>
        )}

        {(activeTab === 'by_zone' || activeTab === 'by_phase' || activeTab === 'versions' || activeTab === 'documents' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description={`Dữ liệu ${tabs.find(t => t.value === activeTab)?.label} đang được liên kết từ các phân hệ liên quan để đảm bảo tính nhất quán trên toàn dự án.`}
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
